import { DataSource } from '@angular/cdk/table'
import { ErrorHandler, Injectable } from '@angular/core'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { BehaviorSubject, Observable, combineLatest, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { AsiakkaanTuote } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'

interface Hakutiedot {
  sivutustiedot: PageEvent
  lajittelutiedot: Sort
}

/**
 * Read-only datasource lemonator-puolen tuoteselaukselle. Kysyy customers/{asiakasAvain}/lasku-tuotteet
 * Lemonaid-firestoresta nykyisen valitun asiakkaan avaimella; lajittelu tehdään client-puolella,
 * jotta firestore-indeksitarpeet pysyvät minimissä.
 *
 * Pagination: ladataan kaikki (per asiakas tyypillisesti pieni määrä) ja sivutetaan client-puolella.
 */
@Injectable()
export class TuotteetSelaaDataSourceService extends DataSource<AsiakkaanTuote> {

  private _hakutiedot: Hakutiedot = {
    sivutustiedot: { length: 0, pageIndex: 0, pageSize: 20 },
    lajittelutiedot: { active: 'nimi', direction: 'asc' },
  }
  private _hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this._hakutiedot)
  hakutiedotObservable: Observable<Hakutiedot> = this._hakutiedotSubject.asObservable()

  private _lataaSubject = new BehaviorSubject<boolean>(true)
  lataaObservable: Observable<boolean> = this._lataaSubject.asObservable()

  private _rivienMaaraSubject = new BehaviorSubject<number>(0)
  rivienMaaraObservable: Observable<number> = this._rivienMaaraSubject.asObservable()

  /** Kaikki ladatut tuotteet ennen client-puolen suodatusta. */
  private _kaikkiTuotteetObservable: Observable<AsiakkaanTuote[]>

  /** Suodatettu + lajiteltu + sivutettu tuotelista, näytettäväksi taulukossa. */
  nakyvatTuotteetObservable: Observable<AsiakkaanTuote[]>

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasService: AsiakasService,
    private _laskuUriService: LaskuUriService,
  ) {
    super()

    this._kaikkiTuotteetObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      tap(() => this._lataaSubject.next(true)),
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return observableOf<AsiakkaanTuote[]>([])
        }
        const uri = this._laskuUriService.getLaskuTuotteetCollectionUri(avainTiedot.avain)
        return this._firebaseLemonaid.firestoreCollection<AsiakkaanTuote>(uri).listenSnapshots().pipe(
          map(snapshots => snapshots.map(snap => {
            const tuote = snap.data()
            tuote.$key = snap.id
            return tuote
          })),
        )
      }),
      tap(() => this._lataaSubject.next(false)),
      lemonShare(),
    )

    this.nakyvatTuotteetObservable = combineLatest([this._kaikkiTuotteetObservable, this._hakutiedotSubject]).pipe(
      map(([tuotteet, hakutiedot]) => {
        // Poistetut ulos
        let suodatetut = tuotteet.filter(t => !t.poistettu)

        // Lajittelu
        const { active, direction } = hakutiedot.lajittelutiedot
        if (active && direction) {
          const suunta = direction === 'asc' ? 1 : -1
          suodatetut = suodatetut.slice().sort((a, b) => this._vertaaKentat(a, b, active) * suunta)
        }

        this._rivienMaaraSubject.next(suodatetut.length)

        const { pageIndex, pageSize } = hakutiedot.sivutustiedot
        const alku = pageIndex * pageSize
        return suodatetut.slice(alku, alku + pageSize)
      }),
      lemonShare(),
    )
  }

  private _vertaaKentat(a: AsiakkaanTuote, b: AsiakkaanTuote, kentta: string): number {
    if (kentta === 'hinta') {
      return (a.hinta ?? 0) - (b.hinta ?? 0)
    }
    const arvoA = String((a as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    const arvoB = String((b as unknown as Record<string, unknown>)[kentta] ?? '').toLowerCase()
    return arvoA.localeCompare(arvoB)
  }

  setSort(sort: Sort) {
    this._hakutiedot = { ...this._hakutiedot, lajittelutiedot: sort, sivutustiedot: { ...this._hakutiedot.sivutustiedot, pageIndex: 0 } }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  setPage(page: PageEvent) {
    this._hakutiedot = { ...this._hakutiedot, sivutustiedot: page }
    this._hakutiedotSubject.next(this._hakutiedot)
  }

  get sort(): Sort {
    return this._hakutiedot.lajittelutiedot
  }

  connect(): Observable<AsiakkaanTuote[]> {
    return this.nakyvatTuotteetObservable
  }

  disconnect() {}
}
